import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import '../../components/admin/AdminLayout.css';

const AdminUserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [documentCounts, setDocumentCounts] = useState({ bills: 0, invoices: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get(`/admin/users/${id}`);
      setUserData(response.data.user);
      setDocumentCounts(response.data.documentCounts);
      setEditForm({
        firstName: response.data.user.firstName,
        lastName: response.data.user.lastName,
        email: response.data.user.email,
        phone: response.data.user.phone || ''
      });
    } catch (error) {
      toast.error('Failed to fetch user');
      navigate('/admin/users');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleSave = async () => {
    try {
      await api.put(`/admin/users/${id}`, editForm);
      toast.success('User updated');
      setEditing(false);
      fetchUser();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to DELETE this user? This action cannot be undone.')) return;
    if (!window.confirm('This will also delete all their documents. Are you absolutely sure?')) return;
    
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success('User deleted');
      navigate('/admin/users');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleMakeAdmin = async () => {
    if (!window.confirm('Make this user an admin?')) return;
    
    try {
      await api.post(`/admin/make-admin/${id}`);
      toast.success('User is now an admin');
      fetchUser();
    } catch (error) {
      toast.error('Failed to make admin');
    }
  };

  const handleRemoveAdmin = async () => {
    if (!window.confirm('Remove admin role from this user?')) return;
    
    try {
      await api.post(`/admin/remove-admin/${id}`);
      toast.success('Admin role removed');
      fetchUser();
    } catch (error) {
      toast.error('Failed to remove admin');
    }
  };

  const handleResetSubscription = async () => {
    if (!window.confirm('Reset this user\'s subscription to FREE? This will clear all subscription data including Stripe IDs.')) return;
    if (!window.confirm('Are you sure? This is for TESTING purposes only.')) return;
    
    try {
      await api.post(`/admin/users/${id}/reset-subscription`);
      toast.success('Subscription reset to free');
      fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset subscription');
    }
  };

  const getSubscriptionStatus = () => {
    if (userData.isPromoter) {
      const expires = userData.promoterExpiresAt 
        ? new Date(userData.promoterExpiresAt).toLocaleDateString()
        : 'Lifetime';
      return `🎁 Promoter (${userData.promoterType}) - ${expires}`;
    }
    if (userData.subscription?.plan === 'premium' && userData.subscription?.status === 'active') {
      const billingType = userData.subscription?.billingPeriod === 'yearly' ? 'Yearly' : 'Monthly';
      return `⭐ ${billingType} Premium (expires ${new Date(userData.subscription.endDate).toLocaleDateString()})`;
    }
    return 'Free';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading user...</div>
      </AdminLayout>
    );
  }

  if (!userData) {
    return (
      <AdminLayout>
        <div className="admin-empty">User not found</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '20px' }}>
        <button 
          className="admin-btn admin-btn-secondary"
          onClick={() => navigate('/admin/users')}
        >
          ← Back to Users
        </button>
      </div>

      <div className="admin-user-detail">
        <div>
          {/* User Info Card */}
          <div className="admin-user-info-card">
            <h3>User Information</h3>
            
            {editing ? (
              <>
                <div className="admin-form-group">
                  <label className="admin-form-label">First Name</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={editForm.firstName}
                    onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Last Name</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={editForm.lastName}
                    onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Email</label>
                  <input
                    type="email"
                    className="admin-form-input"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">Phone</label>
                  <input
                    type="text"
                    className="admin-form-input"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button className="admin-btn admin-btn-primary" onClick={handleSave}>Save</button>
                  <button className="admin-btn admin-btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="admin-user-info-row">
                  <span className="admin-user-info-label">Name</span>
                  <span className="admin-user-info-value">{userData.firstName} {userData.lastName}</span>
                </div>
                <div className="admin-user-info-row">
                  <span className="admin-user-info-label">Email</span>
                  <span className="admin-user-info-value">{userData.email}</span>
                </div>
                <div className="admin-user-info-row">
                  <span className="admin-user-info-label">Phone</span>
                  <span className="admin-user-info-value">{userData.phone || 'Not provided'}</span>
                </div>
                <div className="admin-user-info-row">
                  <span className="admin-user-info-label">Joined</span>
                  <span className="admin-user-info-value">{new Date(userData.createdAt).toLocaleDateString()}</span>
                </div>
                <button 
                  className="admin-btn admin-btn-secondary"
                  onClick={() => setEditing(true)}
                  style={{ marginTop: '16px' }}
                >
                  Edit User
                </button>
              </>
            )}
          </div>

          {/* Status Card */}
          <div className="admin-user-info-card" style={{ marginTop: '20px' }}>
            <h3>Account Status</h3>
            <div className="admin-user-info-row">
              <span className="admin-user-info-label">Role</span>
              <span className="admin-user-info-value">
                {userData.role === 'super_admin' ? '👑 Super Admin' : 
                 userData.role === 'admin' ? '🛡️ Admin' : 'User'}
              </span>
            </div>
            <div className="admin-user-info-row">
              <span className="admin-user-info-label">Subscription</span>
              <span className="admin-user-info-value">{getSubscriptionStatus()}</span>
            </div>
            <div className="admin-user-info-row">
              <span className="admin-user-info-label">Status</span>
              <span className="admin-user-info-value">
                {userData.isSuspended ? (
                  <span className="admin-badge suspended">🚫 Suspended</span>
                ) : (
                  <span className="admin-badge active">✓ Active</span>
                )}
              </span>
            </div>
            
            {userData.isPromoter && (
              <>
                <div className="admin-user-info-row">
                  <span className="admin-user-info-label">Promoter Type</span>
                  <span className="admin-user-info-value">{userData.promoterType}</span>
                </div>
                {userData.promoterNotes && (
                  <div className="admin-user-info-row">
                    <span className="admin-user-info-label">Notes</span>
                    <span className="admin-user-info-value">{userData.promoterNotes}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div>
          {/* Documents Card */}
          <div className="admin-user-info-card">
            <h3>Documents</h3>
            <div className="admin-user-info-row">
              <span className="admin-user-info-label">Bills of Sale</span>
              <span className="admin-user-info-value">{documentCounts.bills}</span>
            </div>
            <div className="admin-user-info-row">
              <span className="admin-user-info-label">Invoices</span>
              <span className="admin-user-info-value">{documentCounts.invoices}</span>
            </div>
            <div className="admin-user-info-row">
              <span className="admin-user-info-label">Total Generated</span>
              <span className="admin-user-info-value">{userData.documentsGenerated || 0}</span>
            </div>
          </div>

          {/* Admin Actions Card */}
          <div className="admin-user-info-card" style={{ marginTop: '20px' }}>
            <h3>Admin Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              
              {/* Role Management - Only for super_admin */}
              {currentUser?.role === 'super_admin' && userData.role !== 'super_admin' && (
                <>
                  {userData.role === 'user' ? (
                    <button 
                      className="admin-btn admin-btn-primary"
                      onClick={handleMakeAdmin}
                    >
                      🛡️ Make Admin
                    </button>
                  ) : (
                    <button 
                      className="admin-btn admin-btn-secondary"
                      onClick={handleRemoveAdmin}
                    >
                      Remove Admin Role
                    </button>
                  )}
                </>
              )}
              
              {/* Delete User - Only for super_admin */}
              {currentUser?.role === 'super_admin' && userData.role !== 'super_admin' && (
                <button 
                  className="admin-btn admin-btn-danger"
                  onClick={handleDelete}
                >
                  🗑️ Delete User
                </button>
              )}

              {/* Reset Subscription - Only for super_admin (testing) - allow self-reset */}
              {currentUser?.role === 'super_admin' && (userData.role !== 'super_admin' || userData._id === currentUser?.id) && (
                <button 
                  className="admin-btn admin-btn-secondary"
                  onClick={handleResetSubscription}
                  style={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                >
                  🔄 Reset Subscription (Test)
                </button>
              )}
              
              {userData.role === 'super_admin' && userData._id !== currentUser?.id && (
                <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                  Super admin accounts cannot be modified.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUserDetail;
